import React, { useEffect } from 'react';

import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import useForm from 'src/hooks/useForm';
import { type Modification } from 'src/services/modification/types';

import {
  MAX_DESCRIPTION_CHAR_LENGTH,
  MAX_DESCRIPTION_ERR_MSG,
  MAX_NAME_CHAR_LENGTH,
  MAX_NAME_ERR_MSG,
} from '../constants';

import JobModificationFormFields from './modificationFormFields';

interface JobModificationModalProps {
  selectedModification: Modification | null;
  isOpen: boolean;
  onSave: (data: Modification) => Promise<void>;
  onCancel: () => void;
}

const JobModificationModal = ({ selectedModification, isOpen, onSave, onCancel }: JobModificationModalProps) => {
  const { validateForm, handleChange, resetForm, setFormValues, formData, errors } = useForm<Modification>({
    validations: {
      name: {
        required: {
          value: true,
          message: 'Name is required',
        },
        custom: {
          isValid: (value) => value.length <= MAX_NAME_CHAR_LENGTH,
          message: MAX_NAME_ERR_MSG,
        },
      },
      description: {
        required: {
          value: false,
        },
        custom: {
          isValid: (value) => value.length <= MAX_DESCRIPTION_CHAR_LENGTH,
          message: MAX_DESCRIPTION_ERR_MSG,
        },
      },
    },
    initialValues: {
      name: '',
      description: '',
      isFreeFormTextAllowed: false,
    },
  });

  useEffect(() => {
    if (selectedModification) {
      setFormValues(selectedModification);
    } else {
      resetForm();
    }
  }, [selectedModification]);

  const save = async () => {
    if (validateForm()) {
      await onSave(formData);
      resetForm();
    }
  };

  const cancel = () => {
    onCancel();
    resetForm();
  };

  return (
    <Modal
      title={selectedModification ? 'Edit Job Modification' : 'Add New Job Modification'}
      open={isOpen}
      onClose={onCancel}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <JobModificationFormFields formData={formData} errors={errors} handleChange={handleChange} />
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button type="secondary" size="small" onClick={cancel}>
            Cancel
          </Button>
          <Button type="primary" size="small" onClick={save}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default JobModificationModal;
